import _find from 'lodash/find';

import {
  FileAudio,
  FileDoc,
  FileMedia,
  FilePdf,
  FilePpt,
  FileSheet,
  FileVideo,
  FileWeblink,
  FileZipColored,
  Folder,
  Youtube,
} from '@lyearn/icons';
import { isImage } from '@/components/LegacyMediaContainer/utils';
import { MediaItem } from '@/components/Media/components/MediaEntity/types';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import {
  MediaKindEnum,
  ResourceCategoryEnum,
  ResourceKindEnum,
  ResourceResponseTypeEnum,
} from '@/types/schema';
type FileType = { icon: typeof Folder; types: string[]; category: ResourceCategoryEnum };

const fileTypeMap: FileType[] = [
  { icon: Folder, types: ['folder'], category: ResourceCategoryEnum.Folder },
  { icon: FileAudio, types: ['audio/'], category: ResourceCategoryEnum.Audio },
  { icon: FilePdf, types: ['application/pdf'], category: ResourceCategoryEnum.Document },
  { icon: FileZipColored, types: ['application/zip'], category: ResourceCategoryEnum.Document },
  { icon: FileVideo, types: ['video/'], category: ResourceCategoryEnum.Video },
  { icon: FileMedia, types: ['image/'], category: ResourceCategoryEnum.Image },
  // ref https://docs.microsoft.com/en-us/previous-versions/office/office-2007-resource-kit/ee309278(v=office.12)?redirectedfrom=MSDN
  {
    icon: FileSheet,
    types: [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml',
    ],
    category: ResourceCategoryEnum.Document,
  },
  {
    icon: FilePpt,
    types: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml',
    ],
    category: ResourceCategoryEnum.Document,
  },
];

const getFileType = (mimeType?: string) =>
  _find(fileTypeMap, ({ types }) => types.find((type) => mimeType?.indexOf(type) === 0)) as
    | FileType
    | undefined;

export const getFileIcon = (
  mimeType?: string,
  kind: MediaKindEnum | ResourceKindEnum = ResourceKindEnum.File,
  category?: ResourceCategoryEnum,
) => {
  if (kind === MediaKindEnum.Folder || kind === ResourceKindEnum.Folder) {
    return Folder;
  }
  if (kind === MediaKindEnum.WebLink) {
    return FileWeblink;
  }
  // todo: This should be returned from the plugin and
  //  no mention of youtube anywhere other than in plugins/YouTube + place where it's imported
  if (category === ResourceCategoryEnum.YoutubeLink) {
    return Youtube;
  }
  const fileIcon = getFileType(mimeType);

  if (fileIcon?.icon) {
    return fileIcon.icon;
  }

  return FileDoc;
};

export const getFileIconForMedia = (media: EntityMediaFragment) => {
  const { kind, mimeType } = media;
  const fileCategory = getFileCategory(mimeType!);

  return getFileIcon(mimeType!, kind, fileCategory);
};

export const getFileCategory = (mimeType?: string): ResourceCategoryEnum => {
  return getFileType(mimeType)?.category || ResourceCategoryEnum.Document;
};

export const getAcceptProp = (accept: ResourceResponseTypeEnum | string) => {
  switch (accept) {
    case ResourceResponseTypeEnum.Video:
      return 'video/*';
    case ResourceResponseTypeEnum.Image:
      return 'image/*';
    case ResourceResponseTypeEnum.Audio:
      return 'audio/*';
    default:
      return accept;
  }
};

export const getMediaIcon = (media: MediaItem) => {
  const { kind, url, urlPreview } = media;

  const isWebLink = kind === MediaKindEnum.WebLink;
  const isYoutubeLink = kind === MediaKindEnum.YoutubeLink;
  const linkUrl = urlPreview?.image?.url ?? urlPreview?.icon?.url;
  const isPreviewLink = isWebLink || isYoutubeLink ? linkUrl : isImage(media);
  const Icon = isYoutubeLink ? Youtube : getFileIconForMedia(media);

  const imageSrc = isWebLink || isYoutubeLink ? linkUrl : url;

  return { image: isPreviewLink ? imageSrc : undefined, Icon: Icon };
};
